/**
 * Shared PDF service for handling PDF-related operations
 */

export const PDF_CONFIG = {
  FBC: {
    filename: "FBC.pdf",
    pages: {
      fire_safety: 1,
      egress: 10,
      structural: 20,
      default: 1,
    },
  },
  NFPA: {
    filename: "NFPA-72-2019.pdf",
    pages: {
      alarm_systems: 1,
      detection: 15,
      notification: 30,
      default: 1,
    },
  },
} as const;

export type PDFDocumentType = keyof typeof PDF_CONFIG;
export type FBCSection = keyof typeof PDF_CONFIG.FBC.pages;
export type NFPASection = keyof typeof PDF_CONFIG.NFPA.pages;

export function getPdfFilename(codeStandard: string): string {
  // Handle partial matches for more flexible matching
  if (
    codeStandard.includes("FBC") ||
    codeStandard.includes("CBC") ||
    codeStandard.includes("IBC")
  ) {
    return PDF_CONFIG.FBC.filename;
  }
  if (codeStandard.includes("NFPA 72")) {
    return PDF_CONFIG.NFPA.filename;
  }

  // Fallback to FBC for unknown standards
  return PDF_CONFIG.FBC.filename;
}

export function getPdfPageForSection(
  codeStandard: string,
  section?: string
): number {
  if (codeStandard.includes("NFPA 72")) {
    const nfpaSection = section as NFPASection;
    return PDF_CONFIG.NFPA.pages[nfpaSection] || PDF_CONFIG.NFPA.pages.default;
  }

  const fbcSection = section as FBCSection;
  return PDF_CONFIG.FBC.pages[fbcSection] || PDF_CONFIG.FBC.pages.default;
}

// Shared test data for compliance analysis
export const TEST_COMPLIANCE_DATA = {
  stairs: {
    violations: 3,
    warnings: 1,
    items: [
      {
        type: "violation",
        message: "Stair riser height exceeds maximum 7 inches",
      },
      {
        type: "violation",
        message: "Handrail height not within 34-38 inch range",
      },
      {
        type: "warning",
        message: "Stair width approaching minimum requirement",
      },
      { type: "violation", message: "Missing required landing at door" },
    ],
  },
  doors: {
    violations: 2,
    warnings: 2,
    items: [
      {
        type: "violation",
        message: "Door width below minimum 32 inches clear",
      },
      { type: "warning", message: "Door swing conflicts with adjacent door" },
      { type: "violation", message: "Missing required panic hardware" },
      { type: "warning", message: "Door threshold exceeds 1/2 inch height" },
    ],
  },
  exits: {
    violations: 4,
    warnings: 0,
    items: [
      {
        type: "violation",
        message: "Exit sign not visible from all approaches",
      },
      {
        type: "violation",
        message: "Emergency lighting coverage insufficient",
      },
      {
        type: "violation",
        message: "Exit discharge does not lead to public way",
      },
      {
        type: "violation",
        message: "Travel distance exceeds 250 feet maximum",
      },
    ],
  },
};
